/**
 * 游戏音频系统（2026-10-24 批 1 起，唯一事实源见 docs/音频系统.md）
 *
 * 架构三层：
 * 1. 播放核心：音效走 WebAudio（解码一次复用、并发天然支持、gain 精调），
 *    BGM 走 HTMLAudioElement（流式拉取、不全量解码进内存、原生 loop）。
 * 2. 授权层（可替换）：浏览器/小程序 = 首次用户手势解锁；APK = 壳侧关掉 WebView 自动播放限制后
 *    调 markNativeAuthorized()（Android WebView setMediaPlaybackRequiresUserGesture(false)，
 *    iOS WKWebView mediaPlaybackRequiresUserAction = NO；套壳方案未定，此处只留接口不实现）。
 *    两条路径共用同一套播放核心，只换「什么时候允许出声」。
 * 3. 接线层：app.ts 的 show() 调 onPanelShow()（场景切换收口）；ui.ts 三个按钮工厂挂点击音。
 *    ui.ts 三个按钮工厂用 attachClickSound / playClickSound 挂点击音。
 *
 * 硬约束（审查报告 H-46）：音频不得拖累加载——音效解锁后才预载（约 9KB），
 * BGM 流式播放且不预载；文件全部在 public/audio/ 下独立按需拉取，不进主包。
 *
 * 边界与既有约定的关系：
 * - 本模块不 import app.ts（会形成 app↔audio 循环依赖），面板名用 string。
 * - 音量/静音是设备级偏好：走独立 StorageAdapter 键，不进存档（同 app.ts FAB_POS_KEY 先例），
 *   SaveSystem 零改动——存档键位冲突风险为零。
 * - iOS 静音键策略：不跟随硬件静音开关（用户 2026-10-24 拍板），设置页总开关兜底；
 *    真机行为待实测（见 docs/音频系统.md §5）。
 * - 解锁前不尝试播放（app.ts 启动 show('mainMenu') 发生在任何手势之前）：BGM 只记 pending，
 *    音效静默跳过——保证控制台不报自动播放拦截错误。
 */
import { Platform, StorageAdapter } from '../platform';

/**
 * 音效 id（与 public/audio/ 下文件一一对应，见 SFX_MANIFEST）。
 * 2026-10-25 用户逐个试听后重定：弹窗打开声被移除（`ui.popup` 已删，覆盖层打开不再有音效）。
 */
export type SfxId = 'ui.click' | 'ui.confirm';

/** 音频设置：设备级偏好，独立键持久化（不进存档，见文件头） */
export interface AudioSettings {
    /** BGM 音量 0~1 */
    bgmVolume: number;
    /** 音效音量 0~1 */
    sfxVolume: number;
    /** 总开关（静音）：同步作用于 BGM 与音效 */
    muted: boolean;
}

/** 设置持久化键（设备级偏好，非存档） */
const SETTINGS_KEY = 'audioSettings';

const DEFAULT_SETTINGS: AudioSettings = {
    bgmVolume: 0.5,
    sfxVolume: 0.7,
    muted: false,
};

/** 音效清单：id → 相对路径（相对 vite base './'，子路径部署也成立） */
const SFX_MANIFEST: Record<SfxId, string> = {
    // 用户 2026-10-25 逐轨试听后定稿（源均为 Kenney Interface Sounds，CC0）
    'ui.click': 'audio/ui-click.mp3',     // 源 glitch_004.ogg
    'ui.confirm': 'audio/ui-confirm.mp3', // 源 switch_001.ogg
};

/**
 * BGM 清单：曲目 id → 相对路径。
 * 用户试听拍板后追加条目（授权登记见 docs/音频系统.md §4，随包署名见 public/audio/CREDITS.md）。
 */
const BGM_MANIFEST: Record<string, string> = {
    // 2026-10-25 用户试听拍板：Pixabay「miromaxmusic - Music Promotion」(513944)
    // 入库件是裁掉 18s 前奏 + 尾部 12s 叠回开头的无缝循环段，响度归一到 -14 LUFS（详见 docs/音频系统.md §4.2）
    'bgm.main': 'audio/bgm-main.mp3',
};

/** 默认常驻曲目：全流程单曲循环；无 BGM 时为 null（playBgm(null) = 停播，接线是纯 no-op） */
const BGM_DEFAULT: string | null = 'bgm.main';

/** 常驻曲目 id（供外部查询/测试断言用） */
export const BGM_DEFAULT_ID = 'bgm.main';

/**
 * 场景 → BGM 映射：null = 保持当前曲目不切（弹窗覆盖层用）。
 * 批 1 全流程单曲常驻；批 3 按用户拍板填表（哪些场景要 BGM 后续再定）。
 */
const BGM_BY_PANEL: Record<string, string | null> = {};

/** 首次手势解锁监听的事件（pointerdown 覆盖触摸/鼠标；keydown 覆盖键盘桌面测试） */
const GESTURE_EVENTS = ['pointerdown', 'touchend', 'keydown'] as const;

/** 淡入淡出步进间隔（ms） */
const FADE_STEP_MS = 50;

class AudioManager {
    /** 授权层：是否允许出声（浏览器 = 已收到首个用户手势；APK = 壳侧已置位） */
    private unlocked = false;
    /** 手势解锁探针是否已安装（模块级一次性，防重复 addEventListener） */
    private gestureInstalled = false;
    private gestureHandler: (() => void) | null = null;

    private ctx: AudioContext | null = null;
    private sfxGain: GainNode | null = null;
    private buffers = new Map<SfxId, AudioBuffer>();
    private preloading = false;
    private preloaded = false;

    private bgmAudio: HTMLAudioElement | null = null;
    private bgmId: string | null = null;
    /** 解锁前收到的 BGM 意图：解锁后补播 */
    private pendingBgmId: string | null = null;
    private visibilityInstalled = false;
    private bgmWasPlaying = false;

    private fadeTimer: number | null = null;

    private settings: AudioSettings = { ...DEFAULT_SETTINGS };

    constructor() {
        this.settings = this.loadSettings();
    }

    /**
     * 挂载授权层与可见性策略（app.ts 启动时调一次）。
     * APK 分支不挂手势探针：套壳成型后由壳侧调 markNativeAuthorized() 授权（物理上无手势可等）。
     */
    init(): void {
        this.installVisibility();
        if (Platform.name === 'apk') return;
        this.installGestureUnlock();
    }

    // ---------- 授权层 ----------

    private installGestureUnlock(): void {
        if (this.gestureInstalled) return;
        this.gestureInstalled = true;
        const handler = (): void => { void this.unlockByGesture(); };
        this.gestureHandler = handler;
        GESTURE_EVENTS.forEach(evt =>
            document.addEventListener(evt, handler, { capture: true, passive: true }));
    }

    private detachGestureUnlock(): void {
        if (!this.gestureHandler) return;
        const handler = this.gestureHandler;
        GESTURE_EVENTS.forEach(evt => document.removeEventListener(evt, handler, true));
        this.gestureHandler = null;
    }

    /** 首个手势：resume AudioContext → 预载音效 → 补播解锁前的 BGM 意图 */
    private async unlockByGesture(): Promise<void> {
        if (this.unlocked) return;
        const ctx = this.ensureCtx();
        if (!ctx) return; // 无 WebAudio（极老环境）：静默放弃，不影响页面
        try {
            await ctx.resume();
        } catch {
            return; // resume 被拒（极少）：保留探针，下个手势再试
        }
        this.unlocked = true;
        this.detachGestureUnlock();
        this.preload();
        const pending = this.pendingBgmId;
        this.pendingBgmId = null;
        if (pending) this.playBgm(pending);
    }

    /**
     * APK 原生授权入口（预留，套壳成型后由壳侧在启动时调用）：
     * 壳侧关掉 WebView 自动播放限制之后调这里，音频视为已授权、立即可播。
     * 当前 platform.ts 的 detect() 不返回 'apk'，此入口不会被自动触发。
     */
    markNativeAuthorized(): void {
        if (this.unlocked) return;
        this.unlocked = true;
        this.detachGestureUnlock();
        const ctx = this.ensureCtx();
        ctx?.resume().catch(() => { /* WebView 已放开限制：resume 失败不影响后续播放 */ });
        this.preload();
        const pending = this.pendingBgmId;
        this.pendingBgmId = null;
        if (pending) this.playBgm(pending);
    }

    isUnlocked(): boolean {
        return this.unlocked;
    }

    // ---------- 音效 ----------

    /** 播放一个音效。解锁前/静音/未解码完成都静默跳过（不排队、不报错） */
    playSfx(id: SfxId): void {
        if (!this.unlocked || this.settings.muted) return;
        const buf = this.buffers.get(id);
        if (!buf) return; // 尚在解码（首个手势后的极短窗口）：静默跳过
        const ctx = this.ensureCtx();
        if (!ctx) return;
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(this.sfxGain!);
        src.start();
    }

    private preload(): void {
        if (this.preloading || this.preloaded) return;
        this.preloading = true;
        const ctx = this.ensureCtx();
        if (!ctx) { this.preloading = false; return; }
        const jobs = (Object.keys(SFX_MANIFEST) as SfxId[]).map(id =>
            fetch(SFX_MANIFEST[id])
                .then(res => {
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    return res.arrayBuffer();
                })
                .then(buf => ctx.decodeAudioData(buf))
                .then(decoded => { this.buffers.set(id, decoded); })
                .catch(() => { /* 单个失败静默：该音效无声，不阻塞其他音效 */ })
        );
        void Promise.all(jobs).then(() => {
            this.preloading = false;
            this.preloaded = true;
        });
    }

    // ---------- BGM ----------

    /**
     * 切换 BGM（show() 每次都会调，键没变必须 no-op，不重头播）。
     * 解锁前只记意图（app.ts 启动 show('mainMenu') 在任何手势之前，不能尝试播放）。
     */
    playBgm(id: string | null, fadeMs = 500): void {
        if (id === this.bgmId) return;
        if (id === null) { this.stopBgm(fadeMs); return; }
        const url = BGM_MANIFEST[id];
        if (!url) return; // 未登记曲目不得出现在 public/ 下（授权登记表先行），静默不播
        if (!this.unlocked) { this.pendingBgmId = id; return; }
        this.startBgm(id, url, fadeMs);
    }

    private startBgm(id: string, url: string, fadeMs: number): void {
        const audio = this.bgmAudio ?? (this.bgmAudio = new Audio());
        this.stopFade();
        audio.src = url;
        audio.loop = true;
        audio.volume = 0;
        this.bgmId = id;
        const playP = audio.play();
        const target = this.bgmTargetVolume();
        const onStarted = (): void => this.fade(audio, target, fadeMs);
        if (playP && typeof playP.then === 'function') {
            playP.then(onStarted).catch(() => { /* 起播被拒：保持静音待下个可见性/手势事件 */ });
        } else {
            onStarted(); // 旧浏览器 play() 不返回 Promise
        }
    }

    /** 停 BGM（淡出后暂停，元素保留复用） */
    private stopBgm(fadeMs: number): void {
        const audio = this.bgmAudio;
        this.bgmId = null;
        if (!audio) return;
        this.stopFade();
        if (fadeMs > 0 && !audio.paused) {
            this.fade(audio, 0, fadeMs, () => audio.pause());
        } else {
            audio.pause();
        }
    }

    private bgmTargetVolume(): number {
        return this.settings.muted ? 0 : this.settings.bgmVolume;
    }

    // ---------- 设置（设备级偏好） ----------

    getSettings(): AudioSettings {
        return { ...this.settings };
    }

    setVolume(kind: 'bgm' | 'sfx', value: number): void {
        const v = Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
        if (kind === 'bgm') this.settings.bgmVolume = v;
        else this.settings.sfxVolume = v;
        this.persistSettings();
        this.applyVolumes();
    }

    setMuted(muted: boolean): void {
        this.settings.muted = muted;
        this.persistSettings();
        this.applyVolumes();
    }

    private applyVolumes(): void {
        if (this.sfxGain) this.sfxGain.gain.value = this.settings.muted ? 0 : this.settings.sfxVolume;
        if (this.bgmAudio && this.bgmId) {
            this.fade(this.bgmAudio, this.bgmTargetVolume(), 120);
        }
    }

    private loadSettings(): AudioSettings {
        const raw = StorageAdapter.getItem(SETTINGS_KEY);
        if (!raw) return { ...DEFAULT_SETTINGS };
        try {
            const parsed = JSON.parse(raw) as Partial<AudioSettings>;
            const clamp = (v: unknown, fallback: number): number =>
                typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : fallback;
            return {
                bgmVolume: clamp(parsed.bgmVolume, DEFAULT_SETTINGS.bgmVolume),
                sfxVolume: clamp(parsed.sfxVolume, DEFAULT_SETTINGS.sfxVolume),
                muted: parsed.muted === true,
            };
        } catch {
            return { ...DEFAULT_SETTINGS };
        }
    }

    private persistSettings(): void {
        StorageAdapter.setItem(SETTINGS_KEY, JSON.stringify(this.settings));
    }

    // ---------- 场景收口 ----------

    /**
     * 场景切换收口（app.ts show() 调）：查映射表 → 键没变 no-op → 解锁前只记 pending。
     * 面板名用 string（不 import app.ts，避免循环依赖）。
     */
    onPanelShow(name: string): void {
        const mapped = Object.prototype.hasOwnProperty.call(BGM_BY_PANEL, name)
            ? BGM_BY_PANEL[name]
            : BGM_DEFAULT;
        this.playBgm(mapped);
    }

    // ---------- 可见性（切后台/回前台） ----------

    private installVisibility(): void {
        if (this.visibilityInstalled) return;
        this.visibilityInstalled = true;
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                // 切后台：暂停 BGM 并记状态；音效由 playSfx 的 mute 检查自然静默
                this.bgmWasPlaying = this.bgmId !== null && !!this.bgmAudio && !this.bgmAudio.paused;
                this.bgmAudio?.pause();
            } else {
                // 回前台：已解锁且未静音才恢复（浏览器可能同样拦截，失败静默）
                this.ctx?.resume().catch(() => { /* iOS 回前台 resume 可能被拒：下个手势兜底 */ });
                if (this.bgmWasPlaying && this.unlocked && !this.settings.muted) {
                    this.bgmAudio?.play().catch(() => { /* 下个手势再兜底 */ });
                }
                this.bgmWasPlaying = false;
            }
        });
    }

    // ---------- 内部 ----------

    /** 延迟创建 AudioContext（解锁前不创建：避免自动播放警告） */
    private ensureCtx(): AudioContext | null {
        if (this.ctx) return this.ctx;
        const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (!Ctor) return null;
        try {
            this.ctx = new Ctor() as AudioContext;
            this.sfxGain = this.ctx.createGain();
            this.sfxGain.gain.value = this.settings.muted ? 0 : this.settings.sfxVolume;
            this.sfxGain.connect(this.ctx.destination);
            return this.ctx;
        } catch {
            return null;
        }
    }

    /** 音量渐变（HTMLAudioElement.volume 不支持直接 ramp，用定时器步进） */
    private fade(audio: HTMLAudioElement, to: number, ms: number, onDone?: () => void): void {
        this.stopFade();
        const target = Math.min(1, Math.max(0, to));
        if (ms <= 0) {
            audio.volume = target;
            onDone?.();
            return;
        }
        const steps = Math.max(1, Math.round(ms / FADE_STEP_MS));
        const from = audio.volume;
        let i = 0;
        this.fadeTimer = window.setInterval(() => {
            i += 1;
            const t = i / steps;
            audio.volume = Math.min(1, Math.max(0, from + (target - from) * t));
            if (i >= steps) {
                this.stopFade();
                onDone?.();
            }
        }, FADE_STEP_MS);
    }

    private stopFade(): void {
        if (this.fadeTimer !== null) {
            window.clearInterval(this.fadeTimer);
            this.fadeTimer = null;
        }
    }
}

/** 全局单例（app.ts 与 ui.ts 共享同一播放器） */
export const audioManager = new AudioManager();

/** 上一次点击音的派发时间戳：同一个 click 事件在冒泡链上只响一声 */
let lastClickEventTime = -1;

/**
 * **主行动按钮**的 class 名单：命中即响确认音（`ui.confirm`），其余一律响点击音（`ui.click`）。
 *
 * 口径（用户 2026-10-25 拍板「按主行动按钮自动区分」）：
 * - `btn-primary`：主 CTA（确认行程、下一步、完成造型、再来一周目…）
 * - `btn-post`：发布/提交类（发布微博、删除、解约…）
 * - `pool-cta`：试镜池的行动按钮（签约、完成试镜、购买…）
 * - `btn-danger`：高危主行动（退圈）
 * `menu-btn primary` 是主菜单自己的写法（新游戏），单独判。
 */
const CONFIRM_CLASSES = ['btn-primary', 'btn-post', 'pool-cta', 'btn-danger'];
/** 命中上面名单也要退回点击音的 class：`ghost` = 「离开/返回」这类次要外观 */
const DOWNGRADE_CLASSES = ['ghost'];

/**
 * 判定一颗按钮该响哪个音。
 * **在点击时刻现算**（不在绑定时刻定死）：按钮 class 会随状态变化（页签 active、槽位 filled、
 * 试镜按钮 done/blocked），绑定时快照会把后续状态判错。
 */
function sfxIdFor(el: HTMLElement): SfxId {
    const cl = el.classList;
    if (DOWNGRADE_CLASSES.some(c => cl.contains(c))) return 'ui.click';
    if (cl.contains('menu-btn') && cl.contains('primary')) return 'ui.confirm';
    if (CONFIRM_CLASSES.some(c => cl.contains(c))) return 'ui.confirm';
    return 'ui.click';
}

/**
 * 给元素挂点击音（幂等绑定，dataset 防重复）。
 * 三个 UI 工厂入口（createButton / createBtn / makeClickable）调同一个助手，不各写一份。
 *
 * 为什么是独立 click 监听、而不是把音效包进工厂的 onClick 包装里（实测踩过）：
 * `createButton(cls, text, undefined, icon)` 是既有大量用法（主菜单三个主按钮等），onClick 为 undefined，
 * 调用方之后再自己挂监听——包在 onClick 里会让这些按钮整颗无声。独立监听 + 事件 timeStamp 去重
 * 既能覆盖「工厂给了 onClick」也能覆盖「调用方后挂」，键盘激活原生 button 同样派发 click。
 */
export function attachClickSound(el: HTMLElement): void {
    if (el.dataset.sfxClick === '1') return;
    el.dataset.sfxClick = '1';
    el.addEventListener('click', (e) => {
        if (e.timeStamp === lastClickEventTime) return; // 同一事件沿冒泡链只响一声
        lastClickEventTime = e.timeStamp;
        audioManager.playSfx(sfxIdFor(el));
    });
}

/**
 * 键盘激活路径用（Enter/Space 不派发 click）：不经事件去重，直接响一声。
 * 传 el 时按同一把尺判确认音/点击音；不传则退化为点击音。
 */
export function playClickSound(el?: HTMLElement): void {
    audioManager.playSfx(el ? sfxIdFor(el) : 'ui.click');
}
